import { gkApi } from './index';

const marktApi = gkApi.injectEndpoints({
  endpoints: (builder) => ({
    listMarktArticles: builder.query({
      query: () => ({
        url: `/markt-article/`,
        method: 'get',
        params: { limit: 500 },
      }),
    }),
    listMarktStocks: builder.query({
      query: () => ({
        url: `/markt-stock/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: [{ type: 'MARKT_STOCK' }],
    }),
    listCashRegisters: builder.query({
      query: () => ({
        url: `/cash-register/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: [{ type: 'CASH_REGISTER_LIST' }],
    }),
    openCashRegister: builder.mutation({
      query: (cashRegisterId) => ({
        url: `/cash-register/${cashRegisterId}/open/`,
        method: 'put',
      }),
      invalidatesTags: [
        { type: 'CASH_REGISTER_LIST' },
        { type: 'CASH_SESSION_LIST' },
      ],
    }),
    listCashRegisterSessions: builder.query({
      query: () => ({
        url: `/cash-register/session/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: [{ type: 'CASH_SESSION_LIST' }],
    }),
    getCashSession: builder.query({
      query: (sessionId) => ({
        url: `/cash-register/session/${sessionId}/`,
        method: 'get',
      }),
      providesTags: (result, error, sessionId) => [
        { type: 'CASH_SESSION', id: sessionId },
      ],
    }),
    listCashSessionTransactions: builder.query({
      query: (sessionId) => ({
        url: `/cash-register/session/${sessionId}/transactions/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: (result, error, sessionId) => [
        { type: 'CASH_TX_LIST', id: sessionId },
      ],
    }),
    createCashTransfer: builder.mutation({
      query: ({ sessionId, payload }) => ({
        url: `/cash-register/session/${sessionId}/transfer/`,
        method: 'post',
        body: payload,
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'CASH_REGISTER_LIST' },
        { type: 'CASH_SESSION', id: sessionId },
        { type: 'CASH_TX_LIST', id: sessionId },
      ],
    }),
    closeCashSession: builder.mutation({
      query: ({ sessionId, payload }) => ({
        url: `/cash-register/session/${sessionId}/close/`,
        method: 'post',
        body: payload,
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'CASH_REGISTER_LIST' },
        { type: 'CASH_SESSION_LIST' },
        { type: 'CASH_SESSION', id: sessionId },
        { type: 'CASH_TX_LIST', id: sessionId },
      ],
    }),
    listRegisterSessions: builder.query({
      query: (cashRegisterId) => ({
        url: `/cash-register/${cashRegisterId}/sessions/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: [{ type: 'CASH_SESSION_LIST' }],
    }),
    // On-demand PDFs. Used via the lazy hooks so they fire on click.
    // NB: 'blob' is NOT a valid responseHandler string (RTK only knows
    // 'json'/'text'/'content-type' and would fall back to text()), so we must
    // pass a function to actually get a Blob back.
    getCashRegisterReportPdf: builder.query({
      query: (cashRegisterId) => ({
        url: `/cash-register/${cashRegisterId}/report/`,
        method: 'get',
        responseHandler: (response) => response.blob(),
      }),
    }),
    getCashSessionReportPdf: builder.query({
      query: (sessionId) => ({
        url: `/cash-register/session/${sessionId}/report/`,
        method: 'get',
        responseHandler: (response) => response.blob(),
      }),
    }),
    // All cash transactions of a register (sales + manual transfers) as xlsx.
    // Same blob responseHandler as the PDF endpoints above.
    getCashRegisterExportExcel: builder.query({
      query: (cashRegisterId) => ({
        url: `/cash-register/${cashRegisterId}/export/excel/`,
        method: 'get',
        responseHandler: (response) => response.blob(),
      }),
    }),
    listMarktCustomers: builder.query({
      query: () => ({
        url: `/markt/customer/`,
        method: 'get',
        params: { limit: 500 },
      }),
      providesTags: [{ type: 'MARKT_CUSTOMER_LIST' }],
    }),
    createMarktStockMovement: builder.mutation({
      query: (payload) => ({
        url: '/markt-stock-movement/',
        method: 'post',
        body: payload,
      }),
      invalidatesTags: [{ type: 'MARKT_STOCK' }],
    }),
    createMarktCustomer: builder.mutation({
      query: (payload) => ({
        url: '/markt/customer/',
        method: 'post',
        body: payload,
      }),
      invalidatesTags: [{ type: 'MARKT_CUSTOMER_LIST' }],
    }),
    processTx: builder.mutation({
      query: ({ sessionId, payload }) => ({
        url: `/markt/session/${sessionId}/transaction/`,
        method: 'post',
        body: payload,
      }),
      // Flatten softFail meta flags onto the resolved value so consumers
      // can read tx.tse_sync_required / tx.ereceipt_pending alongside tx.id.
      transformResponse: (data, meta) => ({
        ...(data || {}),
        tse_sync_required: !!meta?.envelope?.tse_sync_required,
        ereceipt_pending: !!meta?.envelope?.ereceipt_pending,
        soft_fail_message: meta?.envelope?.message,
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'MARKT_CUSTOMER_LIST' },
        { type: 'MARKT_STOCK' },
        { type: 'RESERVATION_LIST' },
        // A sale moves cash, so refresh balances + the session ledger.
        { type: 'CASH_REGISTER_LIST' },
        { type: 'CASH_SESSION', id: sessionId },
        { type: 'CASH_TX_LIST', id: sessionId },
      ],
    }),
    // Receipt data + QR data-URIs for printing a customer fiscal receipt.
    // Lazy: fired on demand after a sale or via the reprint button.
    getMarktReceipt: builder.query({
      query: (txId) => ({
        url: `/markt/transaction/${txId}/receipt/`,
        method: 'get',
      }),
    }),
    getReporting: builder.query({
      query: () => ({
        url: `/markt/reporting/`,
        method: 'get',
      }),
    }),
    getCustomerReporting: builder.query({
      query: () => ({
        url: `/markt/reporting/customer/`,
        method: 'get',
        params: { limit: 500 },
      }),
    }),
  }),
});

export const {
  useListMarktArticlesQuery,
  useListMarktStocksQuery,
  useListCashRegistersQuery,
  useOpenCashRegisterMutation,
  useListCashRegisterSessionsQuery,
  useGetCashSessionQuery,
  useListCashSessionTransactionsQuery,
  useCreateCashTransferMutation,
  useCloseCashSessionMutation,
  useListRegisterSessionsQuery,
  useLazyGetCashRegisterReportPdfQuery,
  useLazyGetCashSessionReportPdfQuery,
  useLazyGetCashRegisterExportExcelQuery,
  useListMarktCustomersQuery,
  useCreateMarktStockMovementMutation,
  useCreateMarktCustomerMutation,
  useProcessTxMutation,
  useLazyGetMarktReceiptQuery,
  useGetReportingQuery,
  useGetCustomerReportingQuery,
} = marktApi;
